// Pledge-driven roster (Navaratri saree): a fixed set of slots, one auto-derived sponsor each, matched to
// a pledge on the slot's product variation. Flat table — the slot count is small and bounded.
import React, { useState } from 'react';
import {
  Box, Typography, Paper, Chip, Alert, Button, IconButton, Tooltip,
  Table, TableHead, TableBody, TableRow, TableCell, Link as MuiLink,
} from '@mui/material';
import StickyNote2Icon from '@mui/icons-material/StickyNote2';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PrintIcon from '@mui/icons-material/Print';
import { Link as RouterLink } from 'react-router-dom';
import PageLayout from '../../components/common/PageLayout';
import { useAuth } from '../../contexts/AuthContext';
import {
  fmtDate, fmtDateLong, escHtml, expiresYear, parseAlankaram, printHtml, patch,
  NoteDialog, PledgeHistoryDialog, EmailDialog, RosterHeader, LinkedEventBanner,
} from './shared';

export default function SareeRoster({ slug, data, reload }) {
  const { staff } = useAuth();
  const program = data?.program;
  const event = data?.event;
  const items = data?.items || [];
  const noun = program?.item_noun || 'Item';
  const nounLower = noun.toLowerCase();
  const nounPluralLower = (program?.item_noun_plural || `${noun}s`).toLowerCase();

  const [error, setError] = useState('');
  const [emailCtx, setEmailCtx] = useState(null); // { itemId, label }
  const [pledgeCtx, setPledgeCtx] = useState(null);
  const [noteCtx, setNoteCtx] = useState(null);

  const saveNote = async (text) => {
    if (!noteCtx) return;
    try { await patch(`/api/manage/special-sponsorships/items/${noteCtx.id}`, { sponsor_note: text }); await reload(); setNoteCtx(null); }
    catch (e) { setError(e?.message || 'Failed to save note'); }
  };

  const printRoster = () => {
    const rowsHtml = items.map((it) => {
      const when = it.procession_date ? fmtDateLong(it.procession_date) : '';
      const a = parseAlankaram(it.name);
      const sessionCell = `<td>${escHtml(a.session || '—')}${when ? `<div class="when">${when}</div>` : ''}</td>`;
      if (it.available) return `<tr><td>${escHtml(a.alankaram)}</td>${sessionCell}<td colspan="7"><span class="tbd">Available — no pledge yet</span></td></tr>`;
      const s = it.sponsor || {}; const fam = (s.family || []).map((f) => `${escHtml(f.name)}${f.relation ? ` (${escHtml(f.relation)})` : ''}`).join(', ');
      const p = it.pledge || {};
      const pBal = Number(p.balance || 0);
      const pBalStr = p.status !== 'pledge-completed' && pBal > 0 ? `Balance $${pBal.toLocaleString('en-US')}` : '';
      const pDateStr = p.date ? escHtml(fmtDate(`${p.date}T12:00:00`)) : '';
      const pSub = [pBalStr, pDateStr].filter(Boolean).join(' · ');
      const pledgeCell = p.order_number ? `#${escHtml(p.order_number)}${pSub ? `<div class="sub">${pSub}</div>` : ''}` : '—';
      return `<tr><td>${escHtml(a.alankaram)}</td>${sessionCell}`
        + `<td><b>${escHtml(s.name || '')}</b></td>`
        + `<td>${escHtml(s.spouse || '—')}</td><td>${escHtml(s.gothram || '—')}</td><td>${fam || '—'}</td><td>${pledgeCell}</td><td>${p.status === 'pledge-completed' ? 'Completed' : 'Pending'}</td><td>${expiresYear(p.date) || '—'}</td></tr>`;
    }).join('');
    const evt = event ? `${escHtml(event.title)}${event.event_date ? ` · ${fmtDate(event.event_date)}` : ''}` : '';
    const n = items.filter((i) => !i.available).length;
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>${escHtml(program?.name || 'Sponsors')}</title>
      <style>*{box-sizing:border-box}body{font-family:Georgia,'Times New Roman',serif;color:#1c1c1c;margin:0;padding:28px 32px;line-height:1.45}
      .head{text-align:center;border-bottom:2px solid #9a540f;padding-bottom:12px;margin-bottom:18px}.org{font-size:13px;letter-spacing:.08em;text-transform:uppercase;color:#9a540f}
      h1{font-size:22px;margin:4px 0 2px}.sub{font-size:12px;color:#555}.when{font-size:11px;color:#777;font-style:italic}.tbd{color:#b26a00;font-style:italic}
      table{width:100%;border-collapse:collapse;font-size:12.5px}th{text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:.04em;color:#666;border-bottom:1px solid #ddd;padding:5px 7px}
      td{padding:6px 7px;border-bottom:1px solid #eee;vertical-align:top}.foot{margin-top:18px;font-size:11px;color:#888;text-align:center}@media print{body{padding:0}@page{margin:14mm}}</style></head><body>
      <div class="head"><div class="org">ICCT Memphis · Sri Venkateswara Temple</div><h1>${escHtml(program?.name || 'Sponsors')}</h1>${evt ? `<div class="sub">${evt}</div>` : ''}</div>
      <table><thead><tr><th>${escHtml(noun)}</th><th>Session</th><th>Sponsor</th><th>Spouse</th><th>Gothram</th><th>Family members</th><th>Pledge</th><th>Status</th><th>Expires</th></tr></thead><tbody>${rowsHtml}</tbody></table>
      <div class="foot">${n} sponsored · ${items.length - n} available · generated ${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
      <script>window.onload=function(){setTimeout(function(){window.print()},250)}<\/script></body></html>`;
    const e = printHtml(html); if (e) setError(e);
  };

  return (
    <PageLayout contentMaxWidth="xl" showHeaderImage={false} showOrgHeader>
      <Box sx={{ width: '100%' }}>
        <RosterHeader program={program}>
          <Button variant="outlined" startIcon={<PrintIcon />} onClick={printRoster}>Print roster</Button>
          <Button variant="contained" startIcon={<MailOutlineIcon />} onClick={() => setEmailCtx({ itemId: null, label: `all ${nounPluralLower}` })}>Send recognition email</Button>
        </RosterHeader>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        <LinkedEventBanner event={event} />

        <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow sx={{ '& th': { bgcolor: 'grey.100', fontWeight: 600 } }}>
                <TableCell>{noun}</TableCell><TableCell>Session</TableCell><TableCell>Sponsor</TableCell><TableCell>Spouse</TableCell>
                <TableCell>Pledge</TableCell><TableCell>Status</TableCell><TableCell>Expires</TableCell><TableCell align="center">Note</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((it) => (
                <TableRow key={it.id} hover sx={{ bgcolor: it.available ? 'action.hover' : undefined }}>
                  <TableCell sx={{ fontWeight: 500 }}>{parseAlankaram(it.name).alankaram}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary' }}>{parseAlankaram(it.name).session || '—'}</TableCell>
                  {it.available ? (
                    <TableCell colSpan={5}><Chip size="small" variant="outlined" label="Available — no pledge yet" /></TableCell>
                  ) : (
                    <>
                      <TableCell>
                        <MuiLink component={RouterLink} to={`/devotees/${it.sponsor.customer_id}`} sx={{ fontWeight: 500 }}>{it.sponsor.name || '(sponsor)'}</MuiLink>
                      </TableCell>
                      <TableCell>{it.sponsor.spouse || '—'}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <MuiLink component="button" type="button" onClick={() => setPledgeCtx({ customerId: it.sponsor.customer_id, variationId: it.variation_id, alankaram: it.name, sponsorName: it.sponsor.name })} sx={{ cursor: 'pointer', verticalAlign: 'baseline' }}>#{it.pledge.order_number}</MuiLink>
                        {(() => {
                          const pending = it.pledge.status !== 'pledge-completed';
                          const bal = Number(it.pledge.balance || 0);
                          const balStr = pending && bal > 0 ? `Balance $${bal.toLocaleString('en-US')}` : '';
                          const dateStr = it.pledge.date ? fmtDate(`${it.pledge.date}T12:00:00`) : '';
                          const text = [balStr, dateStr].filter(Boolean).join(' · ');
                          return text ? <Typography variant="caption" color={balStr ? 'warning.main' : 'text.secondary'} sx={{ display: 'block' }}>{text}</Typography> : null;
                        })()}
                      </TableCell>
                      <TableCell>
                        <Chip size="small" variant="outlined" color={it.pledge.status === 'pledge-completed' ? 'success' : 'warning'} label={it.pledge.status === 'pledge-completed' ? 'Completed' : 'Pending'} />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>{expiresYear(it.pledge.date) || '—'}</TableCell>
                    </>
                  )}
                  <TableCell align="center">
                    {it.sponsor_note ? (
                      <Tooltip title={it.sponsor_note}><IconButton size="small" sx={{ color: 'warning.dark' }} onClick={() => setNoteCtx({ id: it.id, title: it.name, current: it.sponsor_note || '' })}><StickyNote2Icon fontSize="small" /></IconButton></Tooltip>
                    ) : (
                      <Tooltip title="Add note"><IconButton size="small" sx={{ opacity: 0.4 }} onClick={() => setNoteCtx({ id: it.id, title: it.name, current: '' })}><NoteAddIcon fontSize="small" /></IconButton></Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
          Sponsors are derived automatically from the pledge on each {nounLower}'s variation — as soon as a pledge is created it appears here. No manual mapping.
        </Typography>
      </Box>

      <EmailDialog open={!!emailCtx} slug={slug} itemId={emailCtx?.itemId} scopeLabel={emailCtx?.label || `all ${nounPluralLower}`} myEmail={staff?.email || ''} onClose={() => setEmailCtx(null)} />
      <PledgeHistoryDialog ctx={pledgeCtx} slug={slug} onClose={() => setPledgeCtx(null)} />
      <NoteDialog ctx={noteCtx} onSave={saveNote} onClose={() => setNoteCtx(null)} />
    </PageLayout>
  );
}
